import { createAsyncThunk } from "@reduxjs/toolkit";
import { UserState } from "@/types";
import { auth } from "@/lib/firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { groupChapters, isValidArray } from "@/lib/utils";
import { httpGet, httpPost } from "@/lib/http";
import { serializedError } from "../helper";

// Convert Firebase user to our User type
export const formatTest = (test: any): any => {
  if (!test) return {};
  return {
    id: test.id,
    detail: test.detail,
    correctAnswers: test.correctAnswers,
    testId: test.id,
    subject: test.detail?.selectedSource?.subject?.[0].name,
    subSubject: test.detail?.selectedSource?.subSubject?.[0].name,
    questions: test.questions || [],
    testType: test.configuration?.submissionType,
    lastVisited: test.detail?.lastVisited,
    isSubmitted: test.detail?.isSubmitted,
    createdAt: test.detail?.createdAt,
    updatedAt: test.detail?.updatedAt,
    result: test.result,
    configuration: test.configuration,
    book: test.detail?.selectedSource?.book?.[0].name,
    chapters: groupChapters(test.detail?.selectedSource?.chapter.map(({ chapterNumber }: { chapterNumber: string }) => chapterNumber))
  }
}

export const fetchUsers = createAsyncThunk(
  "user/fetchUsers",
  async (allTests: boolean | undefined = false, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as { user: UserState };
      let users = state.user.users;

      // Fetch users only if not present
      if (!isValidArray(users)) {
        const res = await httpGet({ url: "admin/users", dispatch });
        users = res?.data?.users || [];
      }

      // If tests not required → return early
      if (!allTests || !users.length) {
        return users;
      }

      // Avoid refetch if already enriched
      if (users.some(u => isValidArray(u.tests))) return users;

      const emails = users.map(u => u.email);

      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < emails.length; i += chunkSize) {
        chunks.push(emails.slice(i, i + chunkSize));
      }

      const testsMap: Record<string, any> = {};

      // 🔥 BULK API CALL IN CHUNKS
      await Promise.all(chunks.map(async (chunk) => {
        const res = await httpPost({
          url: "/test/by-users?practiceTests=true",
          payload: chunk,
          dispatch
        });

        if (res?.data) {
          Object.assign(testsMap, res.data);
        }
      }));

      const enrichedUsers = users.map(user => ({
        ...user,
        tests: (testsMap[user.email] || []).map(formatTest)
      }));

      return enrichedUsers;

    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const updateUser = createAsyncThunk(
  "user/update",
  async (user: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as { user: UserState };
      let users = state.user.users;

      const filterUsers = users.filter(({ id }) => id !== user.id);

      // call api if not already in the state
      const res = await httpPost({ url: 'admin/users', payload: [user], dispatch });
      filterUsers.push(res?.data || []);

      return filterUsers;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);

export const createUser = createAsyncThunk(
  "user/create",
  async (user: any, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as { user: UserState };
      const users = state.user.users;

      // call api if not already in the state
      const res = await httpPost({ url: 'admin/users', payload: [user], dispatch });
      users.push(res?.data || []);

      try {
        await createUserWithEmailAndPassword(auth, user.email, "Welcome@1234");
      } catch (err) {
        console.error('Firebase err', err);
      }

      return users;
    } catch (err: any) {
      return rejectWithValue(serializedError(err));
    }
  }
);
